import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { departmentsAPI, rolesAPI, wardsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Building2, UserCog, Plus, Pencil, Trash2, AlertCircle, Shield, BarChart3, MapPin, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Checkbox } from '../components/ui/checkbox';

/**
 * Access Control — lean Department + Staff Category (Role) CRUD for Hospital/IT Admin & HR.
 * Item 13 from the pre-pharmacy batch.
 * Permission editor is intentionally read-only here; full RBAC editor lands after Pharmacy.
 */
const AccessControlPage = () => {
  const { user } = useAuth();
  const userRoleCode = (user?.role_code || user?.role_name || '').toLowerCase().replace(/\s+/g, '_');
  const allowed = user?.is_super_admin || ['hospital_admin', 'it_admin', 'hr', 'admin'].includes(userRoleCode);

  if (!allowed) {
    return (
      <Card className="max-w-md mx-auto mt-12">
        <CardContent className="py-10 text-center">
          <AlertCircle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-800">Access Restricted</h3>
          <p className="text-sm text-slate-500 mt-1">Only HR / Hospital Admin / IT Admin can manage Departments &amp; Staff Categories.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="access-control-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><UserCog className="h-6 w-6 text-emerald-600" /> Access Control</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage Departments and Staff Categories (Roles). Permission editor for roles ships after Pharmacy.</p>
      </div>

      <Tabs defaultValue="departments">
        <TabsList>
          <TabsTrigger value="departments" data-testid="ac-tab-departments"><Building2 size={14} className="mr-1.5" /> Departments</TabsTrigger>
          <TabsTrigger value="categories" data-testid="ac-tab-categories"><Shield size={14} className="mr-1.5" /> Staff Categories</TabsTrigger>
        </TabsList>
        <TabsContent value="departments"><DepartmentsTab /></TabsContent>
        <TabsContent value="categories"><CategoriesTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export const DepartmentsTab = () => {
  const [items, setItems] = useState([]);
  const [wardsList, setWardsList] = useState([]);  // for default_wards multi-select
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | dept for edit
  const [form, setForm] = useState({
    name: '', code: '', description: '',
    staff_room_available: false,
    staff_room_location: '',
    default_wards: [],         // array of ward ids
    default_specialties: '',   // comma-separated string in form, parsed on save
    standard_medications: '',  // comma- or newline-separated string in form
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        departmentsAPI.list(),
        wardsAPI.list().catch(() => ({ data: [] })),
      ]);
      setItems(r1.data || []);
      setWardsList(r2.data || []);
    } catch { toast.error('Failed to load departments'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (item = null) => {
    setEditing(item || { __new: true });
    setForm({
      name: item?.name || '',
      code: item?.code || '',
      description: item?.description || '',
      staff_room_available: !!item?.staff_room_available,
      staff_room_location: item?.staff_room_location || '',
      default_wards: item?.default_wards || [],
      default_specialties: (item?.default_specialties || []).join(', '),
      standard_medications: (item?.standard_medications || []).join(', '),
    });
  };
  const close = () => { setEditing(null); };

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) { toast.error('Name and code are required'); return; }
    setSaving(true);
    const payload = {
      name: form.name,
      code: form.code.toUpperCase(),
      description: form.description,
      staff_room_available: form.staff_room_available,
      staff_room_location: form.staff_room_location || null,
      default_wards: form.default_wards,
      default_specialties: form.default_specialties.split(',').map(s => s.trim()).filter(Boolean),
      standard_medications: form.standard_medications.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editing?.__new) {
        await departmentsAPI.create(payload);
        toast.success('Department created');
      } else {
        await departmentsAPI.update(editing.id, payload);
        toast.success('Department updated');
      }
      close(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete department "${item.name}"? This cannot be undone.`)) return;
    try {
      await departmentsAPI.delete(item.id);
      toast.success('Department deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-3 mt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{items.length} department{items.length === 1 ? '' : 's'}</p>
        <Button onClick={() => open()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dept-add-btn">
          <Plus size={14} className="mr-1.5" /> Add Department
        </Button>
      </div>
      <Card>
        <CardContent className="pt-5">
          {loading ? (
            <p className="text-center text-slate-400 py-6">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-center text-slate-400 py-6 italic">No departments yet — click Add Department.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="dept-table">
                <thead>
                  <tr className="border-b text-xs uppercase text-slate-500">
                    <th className="text-left py-2 px-2">Name</th>
                    <th className="text-left py-2 px-2">Code</th>
                    <th className="text-left py-2 px-2">Staff Room</th>
                    <th className="text-left py-2 px-2">Default Wards</th>
                    <th className="text-left py-2 px-2">Description</th>
                    <th className="text-right py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(d => (
                    <tr key={d.id} className="border-b hover:bg-slate-50/60" data-testid={`dept-row-${d.id}`}>
                      <td className="py-2 px-2 font-medium text-slate-800">{d.name}</td>
                      <td className="py-2 px-2 font-mono text-xs">{d.code}</td>
                      <td className="py-2 px-2 text-xs">
                        {d.staff_room_available ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700"><Check size={11} /> {d.staff_room_location || 'Available'}</span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-2 px-2 text-xs text-slate-500">{(d.default_wards || []).length || '—'}</td>
                      <td className="py-2 px-2 text-slate-600 text-xs">{d.description || '—'}</td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <Link to={`/department-dashboard?department_id=${d.id}`}>
                          <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50" title="View Dashboard" data-testid={`dept-view-dashboard-${d.id}`}>
                            <BarChart3 size={14} />
                          </Button>
                        </Link>
                        <Button size="sm" variant="ghost" onClick={() => open(d)} data-testid={`dept-edit-${d.id}`}><Pencil size={14} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(d)} className="text-red-500 hover:bg-red-50" data-testid={`dept-delete-${d.id}`}><Trash2 size={14} /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.__new ? 'Add Department' : 'Edit Department'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Cardiology" data-testid="dept-form-name" />
              </div>
              <div>
                <Label>Code *</Label>
                <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="CARD" maxLength={20} data-testid="dept-form-code" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} data-testid="dept-form-desc" />
            </div>

            <div className="border-t pt-3 mt-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Department Dashboard Inputs</p>

              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="dept-form-staff-room" checked={form.staff_room_available} onCheckedChange={(v) => setForm(f => ({ ...f, staff_room_available: !!v }))} data-testid="dept-form-staff-room-available" />
                <Label htmlFor="dept-form-staff-room" className="cursor-pointer">Dedicated Staff Room Available</Label>
              </div>
              {form.staff_room_available && (
                <div className="mb-3">
                  <Label className="flex items-center gap-1"><MapPin size={11} /> Staff Room Location</Label>
                  <Input value={form.staff_room_location} onChange={e => setForm(f => ({ ...f, staff_room_location: e.target.value }))} placeholder="Block B · 2nd Floor · Room 204" data-testid="dept-form-staff-room-location" />
                </div>
              )}

              <div className="mb-3">
                <Label>Default Wards <span className="text-[10px] text-slate-400 ml-1">(used to scope bed inventory & admissions on the dashboard)</span></Label>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2 mt-1 grid grid-cols-2 gap-1.5" data-testid="dept-form-wards-list">
                  {wardsList.length === 0 && <p className="text-xs text-slate-400 col-span-2 italic py-2">No wards defined yet.</p>}
                  {wardsList.map(w => {
                    const checked = form.default_wards.includes(w.id);
                    return (
                      <label key={w.id} className="flex items-center gap-1.5 cursor-pointer text-xs hover:bg-slate-50 rounded px-1 py-0.5">
                        <Checkbox checked={checked} onCheckedChange={(v) => {
                          setForm(f => ({
                            ...f,
                            default_wards: v ? [...f.default_wards, w.id] : f.default_wards.filter(id => id !== w.id),
                          }));
                        }} data-testid={`dept-form-ward-${w.id}`} />
                        <span className="truncate">{w.name}</span>
                        {w.ward_type && <span className="text-[10px] text-slate-400">· {w.ward_type}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="mb-3">
                <Label>Default Specialties <span className="text-[10px] text-slate-400 ml-1">(comma-separated — e.g. <em>Cardiology, Interventional</em>)</span></Label>
                <Input value={form.default_specialties} onChange={e => setForm(f => ({ ...f, default_specialties: e.target.value }))} placeholder="Cardiology, Interventional Cardiology" data-testid="dept-form-specialties" />
              </div>

              <div>
                <Label>Standard Medications / Protocol Set <span className="text-[10px] text-slate-400 ml-1">(comma- or newline-separated drug names)</span></Label>
                <Textarea value={form.standard_medications} onChange={e => setForm(f => ({ ...f, standard_medications: e.target.value }))} rows={3} placeholder="Aspirin 75mg&#10;Atorvastatin 20mg&#10;Metoprolol 25mg" data-testid="dept-form-meds" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dept-form-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CategoriesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rolesAPI.list();
      setItems(res.data || []);
    } catch { toast.error('Failed to load staff categories'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (item = null) => {
    setEditing(item || { __new: true });
    setForm({ name: item?.name || '', code: item?.code || '', description: item?.description || '' });
  };
  const close = () => { setEditing(null); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (editing?.__new && !form.code.trim()) { toast.error('Code is required for new categories'); return; }
    setSaving(true);
    try {
      if (editing?.__new) {
        await rolesAPI.create({ name: form.name, code: form.code.toLowerCase().replace(/\s+/g, '_'), permissions: [], description: form.description });
        toast.success('Staff category created');
      } else {
        await rolesAPI.update(editing.id, { name: form.name, description: form.description });
        toast.success('Staff category updated');
      }
      close(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  const remove = async (item) => {
    if (item.is_system) { toast.error('System categories cannot be deleted'); return; }
    if (!window.confirm(`Delete staff category "${item.name}"? Users on this category must be reassigned first.`)) return;
    try {
      await rolesAPI.delete(item.id);
      toast.success('Staff category deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-3 mt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{items.length} staff categor{items.length === 1 ? 'y' : 'ies'}</p>
        <Button onClick={() => open()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="role-add-btn">
          <Plus size={14} className="mr-1.5" /> Add Staff Category
        </Button>
      </div>
      <Card>
        <CardContent className="pt-5">
          {loading ? (
            <p className="text-center text-slate-400 py-6">Loading…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="role-table">
                <thead>
                  <tr className="border-b text-xs uppercase text-slate-500">
                    <th className="text-left py-2 px-2">Name</th>
                    <th className="text-left py-2 px-2">Code</th>
                    <th className="text-left py-2 px-2">Permissions</th>
                    <th className="text-left py-2 px-2">Type</th>
                    <th className="text-right py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(r => (
                    <tr key={r.id} className="border-b hover:bg-slate-50/60" data-testid={`role-row-${r.id}`}>
                      <td className="py-2 px-2 font-medium text-slate-800">{r.name}</td>
                      <td className="py-2 px-2 font-mono text-xs">{r.code}</td>
                      <td className="py-2 px-2 text-slate-600 text-xs">{(r.permissions || []).length} permission{(r.permissions || []).length === 1 ? '' : 's'}</td>
                      <td className="py-2 px-2">
                        {r.is_system ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">System</Badge> : <Badge variant="outline">Custom</Badge>}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => open(r)} disabled={r.is_system && !true /* future: allow super-admin override */} data-testid={`role-edit-${r.id}`}><Pencil size={14} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(r)} disabled={r.is_system} className="text-red-500 hover:bg-red-50 disabled:text-slate-300" data-testid={`role-delete-${r.id}`}><Trash2 size={14} /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editing?.__new ? 'Add Staff Category' : 'Edit Staff Category'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Senior Nurse" data-testid="role-form-name" />
            </div>
            {editing?.__new && (
              <div>
                <Label>Code *</Label>
                <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} placeholder="senior_nurse" data-testid="role-form-code" />
                <p className="text-[11px] text-slate-400 mt-1">Lowercase, snake_case identifier.</p>
              </div>
            )}
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} data-testid="role-form-desc" />
            </div>
            <div className="text-[11px] text-slate-500 bg-slate-50 p-2 rounded">
              Permissions are seeded by default. A dedicated permission editor for this category ships after the Pharmacy module.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="role-form-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessControlPage;
